import type {
  InterventionGroupDto,
  PatientResponse,
  PatientRoomResponse,
  StaffProfessionResponse,
  YearPlanResponse
} from '../../../api/generated';
import { isAdmittedGroup } from '../../../services/bedCapacityService';
import { COMPETENCE_CAPACITY_WARNING_MARGIN_HOURS } from '../../../constants/capacityConstants';
import { calculateUsedCompetenceHours } from '../../../services/capacityService';
import type { RoomNeedsAllocation, RoomNeedsPatient } from '../../../services/roomNeedsAllocationService';
import { allocateRoomNeeds } from '../../../services/roomNeedsAllocationService';
import type {
  PlanningIssue,
  ProfessionHours,
  UnscheduledCountState,
  WeekCalculationState,
  WeekKpi
} from './types';

// The chip layout renders a short code; the BE only provides a name, so the
// code is derived from its leading characters (e.g. "Fysioterapeut" → "Fys").
const CODE_LENGTH = 3;

// The chips answer "how many seats are planned in this week": a plain sum of the
// week's group capacities, so a rolled-out plan shows its numbers immediately —
// real patients are only created once someone is booked, long after. Booked
// patients are deliberately not consulted: per-group capacity validation keeps a
// group's bookings within its capacity, so they can never raise this sum.
//
// This is week-local on purpose, with no narrowing across weeks or groups, so it
// differs from REH-332's whole-plan cohort figure (`planPatientCount`, used by
// the roll-out dialog and the change summary). Which of the two rules the header
// should follow is with the domain owner; until that lands, the header counts
// seats and the dialog counts people.

interface WeekSeatCounts {
  readonly patientsCount: number;
  readonly admittedCount: number;
}

// The groups a week actually runs: a plan whose week holds no interventions
// at all is not running that week, whatever its groups' capacities say.
const activeWeekGroups = (
  yearPlan: YearPlanResponse,
  weekNumber: number
): readonly InterventionGroupDto[] =>
  yearPlan.treatmentPlans.flatMap((plan) => {
    const groups = plan.weeks.find((w) => w.weekNumber === weekNumber)?.groups ?? [];
    const hasInterventions = groups.some((g) => (g.interventions ?? []).length > 0);
    return hasInterventions ? groups : [];
  });

const countWeekSeats = (groups: readonly InterventionGroupDto[]): WeekSeatCounts => {
  let patientsCount = 0;
  let admittedCount = 0;
  for (const group of groups) {
    const capacity = group.capacity ?? 0;
    patientsCount += capacity;
    if (isAdmittedGroup(group)) admittedCount += capacity;
  }
  return { patientsCount, admittedCount };
};

// What the bed chip is judged against. The rooms are the location's, and the
// patients and property names the ones the year plan was loaded with.
export interface BedContext {
  readonly rooms: readonly PatientRoomResponse[];
  readonly patientsById: Readonly<Record<string, PatientResponse>>;
  readonly propertyNames: ReadonlyMap<string, string>;
}

// The real patients booked into the week's admitted groups, once each: a
// patient in two of the week's groups still sleeps in one bed. Shadow seats
// hold no one whose needs are known, and a patient missing from the lookup
// (not loaded, or deleted) has no needs to claim, so both stay with the total.
const collectAdmittedPatients = (
  groups: readonly InterventionGroupDto[],
  patientsById: BedContext['patientsById']
): readonly RoomNeedsPatient[] => {
  const byId = new Map<string, RoomNeedsPatient>();
  for (const group of groups) {
    if (!isAdmittedGroup(group)) continue;
    for (const booking of group.assignedPatients ?? []) {
      const patient = booking.shadow ? undefined : patientsById[booking.patientId];
      if (!patient || byId.has(patient.id)) continue;
      byId.set(patient.id, {
        id: patient.id,
        name: patient.name,
        needIds: patient.patientRoomNeedIds
      });
    }
  }
  return [...byId.values()];
};

// Bookings stay within their group's capacity, so every patient placed here
// already holds one of the week's admitted seats. The two checks therefore add
// up to the whole question: if the patients with needs can each get a fitting
// room and the seats do not outnumber the rooms, the rest fit in what is left.
const allocateWeekRoomNeeds = (
  groups: readonly InterventionGroupDto[],
  beds: BedContext
): RoomNeedsAllocation =>
  allocateRoomNeeds(
    collectAdmittedPatients(groups, beds.patientsById),
    beds.rooms.map((room) => ({
      propertyIds: room.patientRoomProperties.map((property) => property.id)
    })),
    beds.propertyNames
  );

// Used hours are real (REH-202); capacity is the staff's configured working
// hours summed per profession and week parity (REH-330, staffCapacityService).
// An undefined capacity map means the roster has not arrived (or failed to
// load): thresholds are omitted so no severity is claimed against a guess.
const computeProfessionHours = (
  yearPlan: YearPlanResponse,
  weekNumber: number,
  kpiProfessions: readonly StaffProfessionResponse[],
  capacityByProfession: ReadonlyMap<string, number> | undefined
): readonly ProfessionHours[] => {
  const usedHours = calculateUsedCompetenceHours(yearPlan, weekNumber);
  return kpiProfessions.map((profession) => ({
    profession: {
      id: profession.id,
      code: profession.name.slice(0, CODE_LENGTH),
      name: profession.name
    },
    planned: usedHours.get(profession.id) ?? 0,
    threshold: capacityByProfession
      ? {
          // A profession nobody holds has an honest 0 h capacity: any planned
          // hour reads as exceeded, nudging staff setup rather than inventing
          // a denominator.
          limit: capacityByProfession.get(profession.id) ?? 0,
          warningMargin: COMPETENCE_CAPACITY_WARNING_MARGIN_HOURS
        }
      : undefined
  }));
};

// Unplanned interventions are an output of a solve, so nothing is claimed until the solve's result
// has actually been read. `unknown` (or an omitted argument) raises no issue, because an unread week
// is not a clean one.
//
// The week's calculation status is deliberately not consulted here. Whether a week has been solved is
// already answered by whoever produced this state: the caller only holds a `known` count for a week
// the backend reported as completed. That includes a started (LOCKED) week, whose solve can still
// have left interventions unplaced. The planner cannot fix it in the app, but hiding it would be
// worse than showing it.
// The canonical "we have not read this week's result" value. A shared constant rather than an object
// literal at the default-parameter position: that allocates a new object per call and reads as though
// the default were mutable state, which is what Sonar flags it for.
export const UNSCHEDULED_UNKNOWN: UnscheduledCountState = { kind: 'unknown' };

const buildPlanningIssues = (unscheduled: UnscheduledCountState): readonly PlanningIssue[] =>
  unscheduled.kind === 'known' && unscheduled.count > 0
    ? [{ code: 'UPLANLAGTE', count: unscheduled.count }]
    : [];

export const computeWeekKpi = (
  yearPlan: YearPlanResponse,
  weekNumber: number,
  staffProfessions: readonly StaffProfessionResponse[],
  calculation: WeekCalculationState,
  capacityByProfession: ReadonlyMap<string, number> | undefined,
  // Omitted by callers that render the header without solver data; an omitted argument reads as
  // `unknown`, which is the conservative direction (no claim rather than a false clean week).
  unscheduled: UnscheduledCountState = UNSCHEDULED_UNKNOWN,
  // The location's patient rooms and who needs them. Undefined until the room list has loaded
  // (or if it failed): the bed chip then carries no threshold and claims nothing, the same rule the
  // profession capacities follow above. Deliberately independent of any solve — the week's admitted
  // seats and bookings are planned, not scheduled, so overbooking is visible before a week has ever
  // been calculated.
  beds?: BedContext
): WeekKpi => {
  // Carried-over plans are read-only here but their patients are in the house all the same.
  const carriedOverPlans = yearPlan.carriedOverPlans?.treatmentPlans ?? [];
  const weekPlan = { ...yearPlan, treatmentPlans: [...yearPlan.treatmentPlans, ...carriedOverPlans] };
  const kpiProfessions = staffProfessions.filter((p) => p.showInKpi);
  const groups = activeWeekGroups(weekPlan, weekNumber);
  const seats = countWeekSeats(groups);
  return {
    weekNumber,
    calculation,
    planningIssues: buildPlanningIssues(unscheduled),
    professions: computeProfessionHours(weekPlan, weekNumber, kpiProfessions, capacityByProfession),
    patientsCount: seats.patientsCount,
    bedsCount: seats.admittedCount,
    // No warningMargin: a full house is expected, only overbooking is a problem.
    // A location with no rooms configured yet gets an honest limit of 0, so any
    // admitted seat reads as exceeded — the same call the profession capacities
    // make rather than inventing a denominator.
    bedsThreshold: beds && { limit: beds.rooms.length },
    roomNeeds: beds && allocateWeekRoomNeeds(groups, beds)
  };
};
